import PropTypes from 'prop-types';
import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Icon } from '@iconify/react';

const TransactionNotFound = ({ transactionId, onGoBack, onGoHome, onRetry }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(2),
      }}
    >
      <Card
        elevation={3}
        sx={{
          maxWidth: 500,
          width: '100%',
          textAlign: 'center',
        }}
      >
        <CardContent
          sx={{
            padding: theme.spacing(4),
            '&:last-child': {
              paddingBottom: theme.spacing(4),
            },
          }}
        >
          {/* Main Icon */}
          <Box
            sx={{
              marginBottom: theme.spacing(3),
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: theme.palette.error.light,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: theme.spacing(1),
              }}
            >
              <Icon
                icon="mdi:receipt-text-remove"
                width={40}
                height={40}
                color={theme.palette.error.main}
              />
            </Box>
          </Box>

          {/* Main Title */}
          <Typography
            variant={isMobile ? 'h5' : 'h4'}
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: theme.palette.text.primary,
              marginBottom: theme.spacing(2),
            }}
          >
            Transaction Not Found
          </Typography>

          {/* Description */}
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              marginBottom: theme.spacing(1),
              lineHeight: 1.6,
            }}
          >
            We couldn&apos;t find the transaction you&apos;re looking for.
          </Typography>

          {/* Transaction ID if provided */}
          {transactionId && (
            <Box
              sx={{
                backgroundColor: theme.palette.grey[100],
                borderRadius: theme.shape.borderRadius,
                padding: theme.spacing(1.5),
                marginBottom: theme.spacing(2),
                border: `1px solid ${theme.palette.grey[300]}`,
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ marginBottom: theme.spacing(0.5) }}
              >
                Transaction ID:
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                  wordBreak: 'break-all',
                }}
              >
                {transactionId}
              </Typography>
            </Box>
          )}

          {/* Action Buttons */}
          <Stack
            direction={isMobile ? 'column' : 'row'}
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            {onRetry && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<Icon icon="mdi:refresh" />}
                onClick={onRetry}
                sx={{
                  minWidth: 120,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Try Again
              </Button>
            )}

            {onGoBack && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Icon icon="mdi:arrow-left" />}
                onClick={onGoBack}
                sx={{
                  minWidth: 120,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Go Back
              </Button>
            )}

            {onGoHome && (
              <Button
                variant="text"
                color="primary"
                startIcon={<Icon icon="mdi:home" />}
                onClick={onGoHome}
                sx={{
                  minWidth: 120,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Home
              </Button>
            )}
          </Stack>

          {/* Help Section */}
          <Box
            sx={{
              marginTop: theme.spacing(3),
              paddingTop: theme.spacing(3),
              borderTop: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              spacing={1}
              sx={{ marginBottom: theme.spacing(1) }}
            >
              <Icon
                icon="mdi:help-circle-outline"
                width={20}
                height={20}
                color={theme.palette.text.secondary}
              />
              <Typography variant="body2" color="text.secondary">
                Need help?
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
              Contact our support team if you believe this is an error
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

TransactionNotFound.propTypes = {
  transactionId: PropTypes.string,
  onGoBack: PropTypes.func,
  onGoHome: PropTypes.func,
  onRetry: PropTypes.func,
};

export default TransactionNotFound;
